// src/pages/DesktopReleasesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Switch, Chip, MenuItem, Select, FormControl,
  InputLabel, Stack, Tooltip, Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { formatDateTime, parseDateTime } from '../utils/formatDateTime';
import {
  fetchDesktopReleases,
  createDesktopRelease,
  updateDesktopRelease,
  deleteDesktopRelease,
  enableDesktopRelease,
  disableDesktopRelease,
} from '../api/apiService';

// ======================== 常量枚举 ========================
const CHANNEL_OPTIONS = ['stable', 'beta', 'internal'];
const PLATFORM_OPTIONS = ['windows'];
const ARCH_OPTIONS = ['x86_64'];

const SEMVER_RE = /^\d+\.\d+\.\d+$/;

const emptyForm = {
  version: '',
  preReleaseId: 1,
  channel: 'stable',
  platform: 'windows',
  arch: 'x86_64',
  artifactUrl: '',
  signature: '',
  notes: '',
  pubDate: new Date(),
  releaseName: '',
  mandatory: false,
  minSupportedVersion: '',
  rolloutPercent: 100,
  isActive: true,
};

// ======================== 表单校验 ========================
function validateForm(form) {
  const errors = {};
  if (!SEMVER_RE.test(form.version)) {
    errors.version = '必须是合法的 SemVer 格式，例如 1.0.0（三段纯数字）';
  }
  if (form.channel && form.channel !== 'stable') {
    const id = Number(form.preReleaseId);
    if (!Number.isInteger(id) || id < 1) {
      errors.preReleaseId = '数字识别码必须是正整数，例如 1';
    }
  }
  if (!form.channel) errors.channel = '请选择渠道';
  if (!form.platform) errors.platform = '请选择平台';
  if (!form.arch) errors.arch = '请选择架构';
  if (!form.artifactUrl || !/^https?:\/\/.+/.test(form.artifactUrl)) {
    errors.artifactUrl = '必须是完整的 https:// 或 http:// 地址';
  }
  if (!form.signature || !form.signature.trim()) {
    errors.signature = '签名内容不能为空（填写 .sig 文件的文本内容）';
  }
  if (form.minSupportedVersion && !SEMVER_RE.test(form.minSupportedVersion)) {
    errors.minSupportedVersion = '必须是合法的 SemVer 格式，例如 1.0.0';
  }
  const pct = Number(form.rolloutPercent);
  if (isNaN(pct) || pct < 0 || pct > 100) {
    errors.rolloutPercent = '必须是 0 到 100 之间的整数';
  }
  return errors;
}

// ======================== 辅助组件：Autocomplete freeSolo ========================
function FreeSoloField({ label, value, onChange, options, error, helperText, required, ...rest }) {
  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value}
      onInputChange={(_, newValue) => onChange(newValue)}
      onChange={(_, newValue) => onChange(newValue || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin="dense"
          fullWidth
          required={required}
          error={!!error}
          helperText={error || helperText}
          {...rest}
        />
      )}
    />
  );
}

// ======================== 主页面 ========================
export default function DesktopReleasesPage() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 筛选条件
  const [filterChannel, setFilterChannel] = useState('');
  const [filterIsActive, setFilterIsActive] = useState('');

  // 弹窗
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // -------- 数据加载 --------
  const loadReleases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterChannel) params.channel = filterChannel;
      if (filterIsActive !== '') params.isActive = filterIsActive;
      const data = await fetchDesktopReleases(params);
      setReleases(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterChannel, filterIsActive]);

  useEffect(() => {
    loadReleases();
  }, [loadReleases]);

  // -------- 表单辅助 --------
  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setFieldEvent = (field) => (e) => setField(field)(e.target.value);

  // -------- 弹窗控制 --------
  const handleOpenCreate = () => {
    setIsEditing(false);
    setCurrentId(null);
    setForm({ ...emptyForm, pubDate: new Date(), preReleaseId: 1 });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setIsEditing(true);
    setCurrentId(row.id);
    // 解析版本号：若含 - 后缀（如 1.0.1-beta.1），拆出 baseVersion 和 preReleaseId
    let baseVersion = row.version || '';
    let preReleaseId = 1;
    if (baseVersion.includes('-')) {
      const dashIdx = baseVersion.indexOf('-');
      const preRelease = baseVersion.slice(dashIdx + 1); // e.g. "beta.1"
      baseVersion = baseVersion.slice(0, dashIdx);       // e.g. "1.0.1"
      const dotIdx = preRelease.lastIndexOf('.');
      if (dotIdx !== -1) {
        preReleaseId = parseInt(preRelease.slice(dotIdx + 1), 10) || 1;
      }
    }
    setForm({
      version: baseVersion,
      preReleaseId,
      channel: row.channel || 'stable',
      platform: row.platform || 'windows',
      arch: row.arch || 'x86_64',
      artifactUrl: row.artifactUrl || '',
      signature: row.signature || '',
      notes: row.notes || '',
      pubDate: parseDateTime(row.pubDate),
      releaseName: row.releaseName || '',
      mandatory: !!row.mandatory,
      minSupportedVersion: row.minSupportedVersion || '',
      rolloutPercent: row.rolloutPercent ?? 100,
      isActive: row.isActive !== undefined ? row.isActive : true,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // -------- 保存 --------
  const handleSave = async () => {
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    try {
      // 组合最终版本号：非 stable 渠道时拼接后缀 -{channel}.{preReleaseId}
      const finalVersion = (form.channel && form.channel !== 'stable')
        ? `${form.version}-${form.channel}.${form.preReleaseId}`
        : form.version;
      const { preReleaseId: _drop, ...restForm } = form;
      const payload = {
        ...restForm,
        version: finalVersion,
        pubDate: form.pubDate instanceof Date ? form.pubDate.toISOString() : form.pubDate,
        rolloutPercent: Number(form.rolloutPercent),
      };
      if (isEditing) {
        await updateDesktopRelease(currentId, payload);
      } else {
        await createDesktopRelease(payload);
      }
      handleCloseDialog();
      loadReleases();
    } catch (err) {
      alert(`保存失败: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // -------- 启用/禁用 --------
  const handleToggleActive = async (row) => {
    try {
      if (row.isActive) {
        await disableDesktopRelease(row.id);
      } else {
        await enableDesktopRelease(row.id);
      }
      loadReleases();
    } catch (err) {
      alert(`操作失败: ${err.message}`);
    }
  };

  // -------- 删除 --------
  const handleDelete = async (id) => {
    if (window.confirm('确定删除此版本记录吗？建议仅在误录入时使用，下线版本请改用"禁用"。')) {
      try {
        await deleteDesktopRelease(id);
        loadReleases();
      } catch (err) {
        alert(`删除失败: ${err.message}`);
      }
    }
  };

  // -------- 表格列 --------
  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'version', headerName: '版本号', width: 100 },
    { field: 'releaseName', headerName: '发布名称', flex: 1, minWidth: 120 },
    {
      field: 'channel',
      headerName: '渠道',
      width: 90,
      renderCell: (params) => {
        const colorMap = { stable: 'success', beta: 'warning', internal: 'default' };
        return <Chip label={params.value} size="small" color={colorMap[params.value] || 'default'} />;
      },
    },
    {
      field: 'platform',
      headerName: '平台/架构',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.platform} / {params.row.arch}</Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: '状态',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? '启用' : '禁用'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'mandatory',
      headerName: '强制更新',
      width: 90,
      renderCell: (params) => (
        <Chip label={params.value ? '是' : '否'} size="small" color={params.value ? 'error' : 'default'} variant="outlined" />
      ),
    },
    {
      field: 'pubDate',
      headerName: '发布时间',
      width: 160,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'updatedAt',
      headerName: '更新时间',
      width: 160,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="编辑">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isActive ? '禁用' : '启用'}>
            <IconButton
              size="small"
              color={params.row.isActive ? 'warning' : 'success'}
              onClick={() => handleToggleActive(params.row)}
            >
              {params.row.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="删除">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const hasFormError = Object.values(formErrors).some(Boolean);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ height: 'calc(100vh - 128px)', width: '100%' }}>
        {/* 顶部：标题 + 筛选 + 新建 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5">版本管理</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>渠道</InputLabel>
              <Select value={filterChannel} label="渠道" onChange={(e) => setFilterChannel(e.target.value)}>
                <MenuItem value="">全部</MenuItem>
                {CHANNEL_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>状态</InputLabel>
              <Select value={filterIsActive} label="状态" onChange={(e) => setFilterIsActive(e.target.value)}>
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="true">启用</MenuItem>
                <MenuItem value="false">禁用</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              发布新版本
            </Button>
          </Stack>
        </Box>

        {/* 错误提示 */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>加载失败: {error}</Alert>}

        {/* 数据表格 */}
        <DataGrid
          rows={releases}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          sx={{ height: 'calc(100% - 56px)' }}
        />

        {/* 新建 / 编辑 Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{isEditing ? '编辑版本记录' : '发布新版本'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {/* 第一行：版本号 + 预发布识别码（非 stable）+ 发布名称 */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="基础版本号"
                  required
                  fullWidth
                  value={form.version}
                  onChange={setFieldEvent('version')}
                  error={!!formErrors.version}
                  helperText={
                    formErrors.version ||
                    (form.channel && form.channel !== 'stable'
                      ? `最终版本号将为：${form.version || '?'}-${form.channel}.${form.preReleaseId || '?'}`
                      : '格式：1.0.0（三段纯数字）')
                  }
                  placeholder="例如：1.0.0"
                />
                {form.channel && form.channel !== 'stable' && (
                  <TextField
                    label="数字识别码"
                    required
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ minWidth: 140 }}
                    value={form.preReleaseId}
                    onChange={setFieldEvent('preReleaseId')}
                    error={!!formErrors.preReleaseId}
                    helperText={formErrors.preReleaseId || '预发布序号，例如 1、2、3'}
                  />
                )}
                <TextField
                  label="发布名称"
                  fullWidth
                  value={form.releaseName}
                  onChange={setFieldEvent('releaseName')}
                  placeholder="例如：EOEZM 1.0.0"
                  helperText="用于展示的版本名称（选填）"
                />
              </Stack>

              {/* 第二行：渠道 + 平台 + 架构 */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FreeSoloField
                  label="渠道 (channel)"
                  required
                  value={form.channel}
                  onChange={setField('channel')}
                  options={CHANNEL_OPTIONS}
                  error={formErrors.channel}
                  helperText="推荐选项: stable / beta / internal"
                />
                <FreeSoloField
                  label="平台 (platform)"
                  required
                  value={form.platform}
                  onChange={setField('platform')}
                  options={PLATFORM_OPTIONS}
                  error={formErrors.platform}
                  helperText="当前仅支持: windows"
                />
                <FreeSoloField
                  label="架构 (arch)"
                  required
                  value={form.arch}
                  onChange={setField('arch')}
                  options={ARCH_OPTIONS}
                  error={formErrors.arch}
                  helperText="当前仅支持: x86_64"
                />
              </Stack>

              {/* 第三行：下载地址 */}
              <TextField
                label="下载地址 (artifactUrl)"
                required
                fullWidth
                value={form.artifactUrl}
                onChange={setFieldEvent('artifactUrl')}
                error={!!formErrors.artifactUrl}
                helperText={formErrors.artifactUrl || '完整的安装包下载 URL，例如：https://download.example.com/EOEZM_1.0.0_x64-setup.exe'}
                placeholder="https://"
              />

              {/* 第四行：签名内容 */}
              <TextField
                label="签名内容 (signature)"
                required
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                value={form.signature}
                onChange={setFieldEvent('signature')}
                error={!!formErrors.signature}
                helperText={formErrors.signature || '填入 .sig 文件的文本内容（非文件路径，非自定义 hash）'}
                placeholder="dW50cnVzdGVkIGNvbW1lbnQ6..."
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />

              {/* 第五行：更新说明 */}
              <TextField
                label="更新说明 (notes)"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={form.notes}
                onChange={setFieldEvent('notes')}
                helperText="选填，支持换行，例如：1. 修复了 xxx 问题\n2. 新增了 yyy 功能"
                placeholder="1. 修复了 xxx 问题&#10;2. 新增了 yyy 功能"
              />

              {/* 第六行：发布时间 + 最低支持版本 + 分发比例 */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <DateTimePicker
                  label="发布时间 (pubDate)"
                  value={form.pubDate}
                  onChange={setField('pubDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: '默认当前时间，RFC3339 格式',
                    },
                  }}
                />
                <TextField
                  label="最低支持版本"
                  fullWidth
                  value={form.minSupportedVersion}
                  onChange={setFieldEvent('minSupportedVersion')}
                  error={!!formErrors.minSupportedVersion}
                  helperText={formErrors.minSupportedVersion || '选填，SemVer 格式，例如：0.9.0'}
                  placeholder="例如：0.9.0"
                />
                <TextField
                  label="分发比例 (rolloutPercent)"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  value={form.rolloutPercent}
                  onChange={setFieldEvent('rolloutPercent')}
                  error={!!formErrors.rolloutPercent}
                  helperText={formErrors.rolloutPercent || '0-100，首版仅存储，不参与实际灰度'}
                />
              </Stack>

              {/* 第七行：开关组 */}
              <Stack direction="row" spacing={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.mandatory}
                      onChange={(e) => setField('mandatory')(e.target.checked)}
                      color="error"
                    />
                  }
                  label="强制更新"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setField('isActive')(e.target.checked)}
                      color="success"
                    />
                  }
                  label="立即启用"
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>取消</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || hasFormError}
            >
              {saving ? <CircularProgress size={20} /> : '保存'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
